import React from "react";
import { getOperationLogDetail } from "@/services/log";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import RightViewLayout from "@/components/RighViewLayout";
import { Box, Typography, Grid, Chip, Paper } from "@mui/material";
import dayjs from "dayjs";

function OperationDetail() {
  const { state } = useLocation();
  const { t } = useTranslation();
  const [logData, setLogData] = React.useState({});

  React.useEffect(() => {
    getOperationLogDetail(state?.id).then((res) => {
      setLogData(res?.data?.data || {});
    });
  }, []);

  const getModuleName = (code) => {
    const moduleKey = `operation_log.modules.${code}`;
    const translatedModule = t(moduleKey);
    return translatedModule !== moduleKey ? translatedModule : code;
  };

  const getStatusColor = (status) => {
    return status == "0" ? "success" : "error";
  };

  const getStatusText = (status) => {
    return status == "0"
      ? t("operation_log.status_success")
      : t("operation_log.status_failed");
  };

  return (
    <RightViewLayout navigateBack={-1} title={t("operation_log.detail_title")}>
      <Paper
        elevation={0}
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          paddingTop: "20px",
          paddingBottom: "36px",
          paddingLeft: "26px",
          border: "1px solid #DDDDDD",
          margin: "0 1rem 2rem 0",
          overflow: "hidden",
        }}>
        <Box marginTop={"20px"}>
          {/* 操作信息 */}
          <Box>
            <Typography variant="fieldLabel">
              {t("operation_log.operation_info")}:
            </Typography>
          </Box>
          <Typography variant="fieldValue">
            {t(logData.userName) || "-"}
          </Typography>

          {/* 操作状态 */}
          <Box mt={2}>
            <Typography variant="fieldLabel">
              {t("operation_log.operation_status")}:
            </Typography>
          </Box>
          <Box mt={1}>
            <Chip
              label={getStatusText(logData.status)}
              color={getStatusColor(logData.status)}
              variant="outlined"
              size="small"
              sx={{
                fontSize: "16px",
              }}
            />
          </Box>

          {/* 操作人员 */}
          <Box mt={2}>
            <Typography variant="fieldLabel">
              {t("operation_log.operator")}:
            </Typography>
          </Box>
          <Typography variant="fieldValue">
            {logData.operName || "-"}
          </Typography>

          {/* 操作人员ID */}
          <Box mt={2}>
            <Typography variant="fieldLabel">
              {t("operation_log.operator_id")}:
            </Typography>
          </Box>
          <Typography variant="fieldValue">{logData.userId || "-"}</Typography>

          {/* 租户编码 */}
          <Box mt={2}>
            <Typography variant="fieldLabel">
              {t("operation_log.tenant_code")}:
            </Typography>
          </Box>
          <Typography variant="fieldValue">
            {logData.tenantId || "-"}
          </Typography>

          {/* 操作时间 */}
          <Box mt={2}>
            <Typography variant="fieldLabel">
              {t("operation_log.operation_time")}:
            </Typography>
          </Box>
          <Typography variant="fieldValue">
            {logData.operationTime
              ? dayjs(logData.operationTime).format("YYYY-MM-DD HH:mm:ss")
              : "-"}
          </Typography>

          {/* 执行时间 */}
          <Box mt={2}>
            <Typography variant="fieldLabel">
              {t("operation_log.execute_time")}:
            </Typography>
          </Box>
          <Typography variant="fieldValue">
            {`${logData?.executeTime || 0} ms`}
          </Typography>

          {/* 主机IP地址 */}
          <Box mt={2}>
            <Typography variant="fieldLabel">
              {t("operation_log.host_ip")}:
            </Typography>
          </Box>
          <Typography variant="fieldValue">{logData.ipAddr || "-"}</Typography>

          {/* 操作地点 */}
          <Box mt={2}>
            <Typography variant="fieldLabel">
              {t("operation_log.operation_location")}:
            </Typography>
          </Box>
          <Typography variant="fieldValue">
            {logData.location || "-"}
          </Typography>

          {/* 操作系统/浏览器 */}
          <Box mt={2}>
            <Typography variant="fieldLabel">
              {t("operation_log.os_browser")}:
            </Typography>
          </Box>
          <Typography variant="fieldValue">
            {`${logData.os || "-"} / ${logData.browser || "-"}`}
          </Typography>

          {/* 请求URL */}
          <Box mt={2}>
            <Typography variant="fieldLabel">
              {t("operation_log.request_url")}:
            </Typography>
          </Box>
          <Typography variant="fieldValue">{logData.url || "-"}</Typography>

          {/* 方法名称 */}
          <Box mt={2}>
            <Typography variant="fieldLabel">
              {t("operation_log.method_name")}:
            </Typography>
          </Box>
          <Typography variant="fieldValue">
            {t(`operation_log.${logData.method}`) || "-"}
          </Typography>

          {/* 请求参数 */}
          <Box mt={2}>
            <Typography variant="fieldLabel">
              {t("operation_log.request_params")}:
            </Typography>
          </Box>
          <Box
            mt={1}
            p={2}
            bgcolor="#f5f5f5"
            borderRadius={1}
            border="1px solid #ddd">
            <Typography
              component="pre"
              variant="body2"
              sx={{
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                fontSize: "12px",
              }}>
              {logData.requestParam || logData.param
                ? JSON.stringify(
                    JSON.parse(logData.requestParam || logData.param),
                    null,
                    2
                  )
                : t("operation_log.no_error_message")}
            </Typography>
          </Box>

          {/* 错误堆栈信息 */}
          <Box mt={2}>
            <Typography variant="fieldLabel">
              {t("operation_log.error_stack")}:
            </Typography>
          </Box>
          <Box
            mt={1}
            p={2}
            bgcolor={logData.errorStack ? "#ffebee" : "#f5f5f5"}
            borderRadius={1}
            border="1px solid #ddd">
            <Typography
              component="pre"
              variant="body2"
              sx={{
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                fontSize: "12px",
                color: logData.errorStack ? "#d32f2f" : "inherit",
              }}>
              {logData.errorStack || t("operation_log.no_error_message")}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </RightViewLayout>
  );
}

export default OperationDetail;
